/**
 * @file
 * Music Explorer Component
 *
 * Handles tab switching and audio playback for the Music Explorer component.
 * - Tab navigation with keyboard support
 * - Audio play/pause functionality
 * - Stops other audio when playing new track
 */

(function (Drupal, once) {
  'use strict';

  /**
   * Currently playing audio element reference.
   * @type {HTMLAudioElement|null}
   */
  let currentAudio = null;

  /**
   * Currently active play button reference.
   * @type {HTMLButtonElement|null}
   */
  let currentPlayButton = null;

  /**
   * Stops any currently playing audio and resets button state.
   */
  function stopCurrentAudio() {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }

    if (currentPlayButton) {
      currentPlayButton.setAttribute('aria-pressed', 'false');
    }

    currentAudio = null;
    currentPlayButton = null;
  }

  /**
   * Switches to a specific tab by target panel ID.
   *
   * @param {HTMLElement} component - The component root element.
   * @param {string} targetPanelId - The paragraph ID of the panel to show.
   */
  function switchTab(component, targetPanelId) {
    const tabs = component.querySelectorAll('.c-music-explorer__tab');
    const panels = component.querySelectorAll('.c-music-explorer__panel');

    if (!tabs.length || !panels.length) {
      return;
    }

    // Stop any playing audio when switching tabs
    stopCurrentAudio();

    // Update tabs
    tabs.forEach(function (tab) {
      const isSelected = tab.getAttribute('data-tab-target') === targetPanelId;

      tab.classList.toggle('is-active', isSelected);
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    // Update panels
    panels.forEach(function (panel) {
      const isVisible = panel.getAttribute('data-panel-id') === targetPanelId;

      if (isVisible) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  /**
   * Gets the tab at a specific index.
   *
   * @param {HTMLElement} component - The component root element.
   * @param {number} index - The tab index.
   * @returns {HTMLElement|null} - The tab element or null.
   */
  function getTabAtIndex(component, index) {
    const tabs = component.querySelectorAll('.c-music-explorer__tab');
    return tabs[index] || null;
  }

  /**
   * Gets the current tab index.
   *
   * @param {HTMLElement} component - The component root element.
   * @returns {number} - The current tab index.
   */
  function getCurrentTabIndex(component) {
    const tabs = component.querySelectorAll('.c-music-explorer__tab');
    let currentIndex = 0;

    tabs.forEach(function (tab, index) {
      if (tab.classList.contains('is-active')) {
        currentIndex = index;
      }
    });

    return currentIndex;
  }

  /**
   * Handles keyboard navigation for tabs.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   * @param {HTMLElement} component - The component root element.
   */
  function handleTabKeydown(event, component) {
    const tabs = component.querySelectorAll('.c-music-explorer__tab');
    const tabCount = tabs.length;

    if (!tabCount) {
      return;
    }

    const currentIndex = getCurrentTabIndex(component);
    let newIndex = currentIndex;
    let handled = false;

    switch (event.key) {
      case 'ArrowLeft':
        newIndex = currentIndex > 0 ? currentIndex - 1 : tabCount - 1;
        handled = true;
        break;

      case 'ArrowRight':
        newIndex = currentIndex < tabCount - 1 ? currentIndex + 1 : 0;
        handled = true;
        break;

      case 'Home':
        newIndex = 0;
        handled = true;
        break;

      case 'End':
        newIndex = tabCount - 1;
        handled = true;
        break;
    }

    if (handled) {
      event.preventDefault();
      const newTab = getTabAtIndex(component, newIndex);
      if (newTab) {
        const targetPanelId = newTab.getAttribute('data-tab-target');
        switchTab(component, targetPanelId);
        newTab.focus();
      }
    }
  }

  /**
   * Toggles audio playback for a play button.
   *
   * @param {HTMLButtonElement} button - The play button element.
   */
  function toggleAudio(button) {
    const audioId = button.getAttribute('data-audio-id');
    const audioSrc = button.getAttribute('data-audio-src');

    if (!audioId || !audioSrc) {
      return;
    }

    const audio = document.getElementById(audioId);

    if (!audio) {
      return;
    }

    const isCurrentlyPlaying = currentAudio === audio && !audio.paused;

    // If clicking on currently playing audio, pause it
    if (isCurrentlyPlaying) {
      audio.pause();
      button.setAttribute('aria-pressed', 'false');
      currentAudio = null;
      currentPlayButton = null;
      return;
    }

    // Stop any other playing audio
    stopCurrentAudio();

    // Play the new audio
    audio.play()
      .then(function () {
        button.setAttribute('aria-pressed', 'true');
        currentAudio = audio;
        currentPlayButton = button;
      })
      .catch(function (error) {
        // Handle autoplay restrictions or other errors
        console.warn('Audio playback failed:', error);
      });
  }

  /**
   * Handles audio ended event.
   *
   * @param {HTMLButtonElement} button - The play button element.
   */
  function handleAudioEnded(button) {
    button.setAttribute('aria-pressed', 'false');

    if (currentPlayButton === button) {
      currentAudio = null;
      currentPlayButton = null;
    }
  }

  /**
   * Initializes the Music Explorer component.
   *
   * @param {HTMLElement} component - The component root element.
   */
  function initMusicExplorer(component) {
    const tabs = component.querySelectorAll('.c-music-explorer__tab');
    const panels = component.querySelectorAll('.c-music-explorer__panel');
    const playButtons = component.querySelectorAll('.c-music-explorer__play-btn');

    if (!tabs.length || !panels.length) {
      return;
    }

    // Set up ARIA relationships between tabs and panels
    tabs.forEach(function (tab, index) {
      const targetPanelId = tab.getAttribute('data-tab-target');
      const panel = component.querySelector('[data-panel-id="' + targetPanelId + '"]');

      if (panel) {
        // Generate unique IDs for ARIA
        const tabId = 'music-tab-' + targetPanelId;
        const panelId = 'music-panel-' + targetPanelId;

        tab.setAttribute('id', tabId);
        tab.setAttribute('aria-controls', panelId);
        panel.setAttribute('id', panelId);
        panel.setAttribute('aria-labelledby', tabId);

        // Show first panel by default
        if (index === 0) {
          panel.removeAttribute('hidden');
        }
      }
    });

    // Tab click handlers
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function (event) {
        event.preventDefault();
        const targetPanelId = tab.getAttribute('data-tab-target');
        switchTab(component, targetPanelId);
      });

      tab.addEventListener('keydown', function (event) {
        handleTabKeydown(event, component);
      });
    });

    // Play button handlers
    playButtons.forEach(function (button) {
      const audioId = button.getAttribute('data-audio-id');
      const audio = audioId ? document.getElementById(audioId) : null;

      button.addEventListener('click', function (event) {
        event.preventDefault();
        toggleAudio(button);
      });

      // Handle keyboard activation
      button.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleAudio(button);
        }
      });

      // Handle audio ended
      if (audio) {
        audio.addEventListener('ended', function () {
          handleAudioEnded(button);
        });

        // Handle audio pause (from external control or tab switching)
        audio.addEventListener('pause', function () {
          if (currentAudio !== audio) {
            button.setAttribute('aria-pressed', 'false');
          }
        });
      }
    });
  }

  /**
   * Drupal behavior for Music Explorer.
   */
  Drupal.behaviors.musicExplorer = {
    attach: function (context) {
      once('music-explorer', '.c-music-explorer', context).forEach(function (component) {
        initMusicExplorer(component);
      });
    },

    detach: function (context, settings, trigger) {
      // Stop audio when component is removed from DOM
      if (trigger === 'unload') {
        stopCurrentAudio();
      }
    }
  };

  // Also stop audio when navigating away from the page
  window.addEventListener('beforeunload', stopCurrentAudio);

})(Drupal, once);
